import React from 'react';
import { FormattedMessage } from 'react-intl';
import _get from 'lodash.get';

import { total_ltt, cmhc_pst } from '@ratehub/mortgage-calculations';


function getCashNeededCalculations({
    homePriceName = 'homePrice',
    downPaymentDollarsName = 'downPaymentDollars',
    homeLocationName = 'homeLocation',

    isFirstTimeBuyerName = 'isFirstTimeBuyer',

    cmhcCalculationName = 'cmhcCalculation',
}) {
    return {
        downPaymentDollarCalculation: {
            id: 'downPaymentDollarCalculation',
            name: 'downPaymentDollarCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.downPaymentCalculationLabel"
                    defaultMessage="Down payment"
                />
            ),
            operandValue: downPaymentDollarsName,
        },
        PSTCalculation: {
            id: 'PSTCalculation',
            name: 'PSTCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.PSTCalculationLabel"
                    defaultMessage="PST on mortgage insurance"
                />
            ),
            operandValue: (values) => getPSTCalculation({
                homeLocation: _get(values, homeLocationName),
                cmhcCalculation: _get(values, cmhcCalculationName),
            }),
            operator: 'plus',
        },
        landTransferCalculation: {
            id: 'landTransferCalculation',
            name: 'landTransferCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.landTransferCalculationLabel"
                    defaultMessage="Land transfer tax"
                />
            ),
            operandValue: (values) => getLandTransferTax({
                homePrice: _get(values, homePriceName),
                homeLocation: _get(values, homeLocationName),
                isFirstTimeBuyer: _get(values, isFirstTimeBuyerName),
            }),
            operator: 'plus',
        },
        lawyerFeeCalculation: {
            id: 'lawyerFeeCalculation',
            name: 'lawyerFeeCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.lawyerFeeCalculationLabel"
                    defaultMessage="Lawyer fees"
                />
            ),
            operandValue: () => 1000,
            operator: 'plus',
        },
        titleInsuranceCalculation: {
            id: 'titleInsuranceCalculation',
            name: 'titleInsuranceCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.TitleInsuranceCalculationLabel"
                    defaultMessage="Title insurance"
                />
            ),
            operandValue: (values) => getTitleInsurance({
                homePrice: _get(values, homePriceName),
            }),
            operator: 'plus',
        },
        homeInspectionCalculation: {
            id: 'homeInspectionCalculation',
            name: 'homeInspectionCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.HomeInspectionCalculationLabel"
                    defaultMessage="Home inspection fees"
                />
            ),
            operandValue: () => 500,
            operator: 'plus',
        },
        appraisalCalculation: {
            id: 'appraisalCalculation',
            name: 'appraisalCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.AppraisalCalculationLabel"
                    defaultMessage="Appraisal fees"
                />
            ),
            operandValue: () => 300,
            operator: 'plus',
        },
        estoppelFeeCalculation: {
            id: 'estoppelFeeCalculation',
            name: 'estoppelFeeCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.EstoppelFeeCalculationLabel"
                    defaultMessage="Estoppel certificate fee"
                />
            ),
            operandValue: () => 100,
            operator: 'plus',
        },
        cashRequiredCalculation: {
            id: 'cashRequiredCalculation',
            name: 'cashRequiredCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasCashNeeded.cashRequiredLabel"
                    defaultMessage="Total cash required"
                />
            ),
            operandValue: (values) => getCashNeeded({
                downPayment: { dollars: _get(values, downPaymentDollarsName) },
                PSTCalculation: values.PSTCalculation,
                lawyerFeeCalculation: values.lawyerFeeCalculation,
                homeInspectionCalculation: values.homeInspectionCalculation,
                appraisalCalculation: values.appraisalCalculation,
                landTransferCalculation: values.landTransferCalculation,
                titleInsuranceCalculation: values.titleInsuranceCalculation,
                estoppelFeeCalculation: values.homeType == 'condo' ? values.estoppelFeeCalculation : 0,
            }),
            operator: 'equals',
            result: true,
        },
    };
}

function getPSTCalculation({ homeLocation, cmhcCalculation }) {
    if (homeLocation?.province?.code && cmhcCalculation) {
        return cmhc_pst(cmhcCalculation, homeLocation.province.code);
    }

    return null;
}

function getLandTransferTax({ homePrice, homeLocation, isFirstTimeBuyer }) {
    return homeLocation?.province?.code && homePrice && isFirstTimeBuyer !== null
        ? total_ltt(homePrice, homeLocation?.province?.code, homeLocation?.slug, isFirstTimeBuyer)
        : null;
}

function getTitleInsurance({ homePrice }) {
    return homePrice * 0.001;
}

function getCashNeeded(values) {
    // PST is based on cmhc which can be null if down payment is over 20%
    const valueArray = [
        values.downPayment?.dollars,
        values.lawyerFeeCalculation,
        values.homeInspectionCalculation,
        values.appraisalCalculation,
        values.landTransferCalculation,
        values.titleInsuranceCalculation,
        values.PSTCalculation ?? 0, // PST can be null if there's no cmhc insurance
        values.estoppelFeeCalculation ?? 0,
    ];

    if (valueArray.some(value => value === null)) {
        return null;
    }

    return valueArray.reduce((sum, value) => sum + value, 0);
}

export default getCashNeededCalculations;
export { getCashNeeded };
